<template>
  <div id="search-form">
    <form @submit.prevent class="container d-flex flex-column">
      <div class="row mb-3">
        <fieldset class="search col-md-3">
          <legend>{{ t('form.search.legend') }}</legend>
          <ncs-select id="documents.subcorpus"/>
          <ncs-elements-type/>
        </fieldset>
        <fieldset class="result col-md-9">
          <legend>{{ t('form.result.legend') }}</legend>
          <div class="row">
            <div class="col-md-3">
              <ncs-result-type/>
            </div>
            <div class="col-md-6">
              <ncs-order-selector/>
            </div>
            <div class="col-md-3">
              <ncs-page-size-selector/>
            </div>
            <div class="col-md-12"
                 v-if="(resultTypeStore.type === 'interventions' || resultTypeStore.type === 'intervention') && elementsStore.type === 'texts'">
              <intervention-filter
                  id="texts.interventions"
                  element-type="texts"
                  element-filter-id="texts.searchable_info"
                  :exclude-infos="['Énfasis', 'Sigla', 'Risa']"
              />
            </div>
            <div class="col-md-offset-3 col-md-6" v-if="resultTypeStore.type === 'matching_expressions'">
              <ncs-group-selector/>
            </div>
          </div>
        </fieldset>
      </div>
      <div class="row mb-3">
        <fieldset class="sensibility col-md-3">
          <legend>{{ $t('form.sensibility.legend') }}</legend>
          <ncs-sensitivity name="ncs_accent"/>
          <ncs-sensitivity name="case"/>
        </fieldset>
        <fieldset class="filters col-md-9">
          <legend>{{ $t('form.filters.legend') }}</legend>
          <div class="row">
            <div class="col-md-4">
              <ncs-select id="texts.grupo_edad"/>
            </div>
            <div class="col-md-4">
              <ncs-number-select-filter type="number" id="texts.edad_from"/>
            </div>
            <div class="col-md-4">
              <ncs-number-select-filter type="number" id="texts.edad_to" :reverse="true"/>
            </div>
            <div class="col-md-4">
              <ncs-select id="texts.rol"/>
            </div>
            <div class="col-md-4">
              <ncs-select id="texts.estudios"/>
            </div>
            <div class="col-md-4">
              <ncs-select id="texts.sexo"/>
            </div>
            <div class="col-md-8">
              <ncs-multi-select id="texts.hablante" :searchable="true"/>
            </div>
            <div class="col-md-4">
              <ncs-select v-if="elementsStore.type === 'units' || elementsStore.type === 'tokens'"
                          :id="`${elementsStore.type}.language`"/>
            </div>
            <div class="col-md-12">
              <intervention-filter
                  v-if="resultTypeStore.type !== 'interventions' && resultTypeStore.type !== 'intervention'"
                  id="tokens.searchable_info"
                  :element-type="elementsStore.type"
                  :element-filter-id="`${elementsStore.type}.searchable_info`"/>
            </div>
          </div>
        </fieldset>
      </div>
      <ncs-element-section/>
      <div class="btn-group align-self-end">
        <ncs-back-button/>
        <button type="button" class="btn btn-primary" @click="reload()">
          {{ $t('reset') }}
        </button>
        <ncs-search-button/>
      </div>
    </form>
    <ncs-result>
      <ncs-frequency-result/>
      <ncs-full-frequency-result/>
      <ncs-kwic-result/>
      <ncs-matching-expressions-result/>
      <ncs-context-result/>
      <interventions-result/>
      <intervention-result/>
    </ncs-result>
  </div>
</template>
<script lang="ts" setup>
import { castArray, isEmpty, each } from 'lodash-es'
import { nextTick, onMounted, watch } from 'vue'
import queryString from 'query-string'
import {
  initialize, useApiStore,
  useElementsStore,
  useFiltersStore,
  useFrequencyGroupsStore,
  useGroupsStore,
  useNcsConfig,
  useResultTypeStore,
  useNcsI18n
} from 'ncs'
import InterventionsResult from './InterventionsResult.vue'
import InterventionResult from './InterventionResult.vue'
import InterventionFilter from './InterventionFilter.vue'

const { t } = useNcsI18n()
const ncsConfig = useNcsConfig()
const resultTypeStore = useResultTypeStore()
const elementsStore = useElementsStore()
const filtersStore = useFiltersStore()
const groupsStore = useGroupsStore()
const frequencyGroupsStore = useFrequencyGroupsStore()
const apiStore = useApiStore()

const showFrequenciesList = async (parsed: any) => {
  resultTypeStore.type = 'matching_expressions'

  // Changing type will reset elements on next tick, so we need to wait for it before setting them
  elementsStore.type = 'tokens'
  await nextTick()
  elementsStore.setElements({ left: [{ token: '*' }] })

  groupsStore.groups = parsed.g as string[]

  const frequencyGroups = castArray(parsed.fg)
  each(ncsConfig.queries.matching_expressions.frequencyGroupValues, (values, group) => {
    if (frequencyGroups.includes(group)) {
      frequencyGroupsStore.frequencyGroups[group] = values
    }
  })
  frequencyGroupsStore.cellValues = ['frequency', 'normalized']
}

onMounted(async () => {
  await initialize()
  const parsed = queryString.parse(window.location.search.substring(1), {
    arrayFormat: 'bracket',
    parseNumbers: true,
    parseBooleans: true
  })
  if (!isEmpty(parsed)) {
    await showFrequenciesList(parsed)
    await apiStore.executeQuery()
  }
})

// Keep language filter value when switching between units and tokens
watch(() => elementsStore.type, async (newValue, oldValue) => {
  if (oldValue === 'units' && newValue === 'tokens' || oldValue === 'tokens' && newValue === 'units') {
    const filterValue = filtersStore.filters[`${oldValue}.language`].value
    filtersStore.resetFilter(`${oldValue}.language`)
    filtersStore.setFilters({ [`${newValue}.language`]: filterValue })
  }
})

// Searchable info filter should be reset when switching between interventions and other types
watch(() => resultTypeStore.type, async (newValue, oldValue) => {
  if (newValue === oldValue ||
      newValue === 'interventions' && oldValue === 'intervention' ||
      newValue === 'intervention' && oldValue === 'interventions') {
    return
  }
  if (newValue === 'interventions' || oldValue === 'interventions') {
    filtersStore.resetFilter('texts.searchable_info')
    filtersStore.resetFilter('units.searchable_info')
    filtersStore.resetFilter('tokens.searchable_info')
  }
})

const reload = () => location.reload()
</script>
